import { Fragment, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { Card } from '../../components/ui/Card'
import { FiltruData } from '../../components/ui/FiltruData'

interface FeeRow {
  id: number
  amazonInvoiceNumber: string
  invoiceDate: string
  period: string | null
  supplierName: string | null
  marketplace: string | null
  currency: string
  bnrRate: number
  amountNet: number
  amountNetRon: number
  vatRate: number
  vatScheme: string | null
  categorie: string | null
  plata: string | null
  status: string
  eaDocId: string | null
  eaError: string | null
  /** Randul din raportul de tranzactii care a retinut factura (publicitatea). */
  retinutaIn: { data: string; decontare: string } | null
}

interface FeesResponse {
  items: FeeRow[]
  summary: {
    count: number
    byCurrency: Record<string, number>
    byStatus: Record<string, number>
    totalRon: number
    peCategorii: Record<string, number>
  }
}

interface FeeDetail {
  lines: { id: number; feeType: string; amount: number }[]
}

interface UploadResult {
  parsed: number
  created: number
  updated: number
  skipped: number
}

interface PublicitateResult {
  fisiere: number
  noi: number
  actualizate: number
  neatinse: number
  erori: { fisier: string; motiv: string }[]
  legate: number
}

const CATEGORII: { key: string; label: string }[] = [
  { key: '', label: 'Toate' },
  { key: 'comision', label: 'Comision vânzare' },
  { key: 'logistica', label: 'Logistică FBA' },
  { key: 'abonament', label: 'Abonamente' },
  { key: 'publicitate', label: 'Publicitate' },
  { key: 'nota_credit', label: 'Note de credit' },
]
const ETICHETA_CATEGORIE = Object.fromEntries(CATEGORII.map((c) => [c.key, c.label]))

const fmt = (n: number) =>
  n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const zi = (s: string) => `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)}`

function eroare(e: unknown, implicit: string): string {
  return (
    (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? implicit
  )
}

/**
 * Amazon → Facturi servicii: facturile Amazon EU (comision, logistică FBA, abonamente, note de
 * credit — CSV „Seller Fee Invoices”) și facturile de publicitate Amazon Ads (PDF), pe file.
 * Design: docs/superpowers/specs/2026-10-31-amazon-incasari-design.md (etapa 2).
 */
export function FacturiComisioaneAmazonPage() {
  const qc = useQueryClient()
  const csvRef = useRef<HTMLInputElement>(null)
  const pdfRef = useRef<HTMLInputElement>(null)
  const [categorie, setCategorie] = useState('')
  const [de, setDe] = useState('')
  const [pana, setPana] = useState('')
  const [deschisa, setDeschisa] = useState<number | null>(null)

  const { data, isLoading } = useQuery<FeesResponse>({
    queryKey: ['amazon-fees', categorie, de, pana],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (categorie) params.set('categorie', categorie)
      if (de) params.set('from', de)
      if (pana) params.set('to', pana)
      return (await api.get(`/amazon/fees?${params}`)).data
    },
  })

  const detaliu = useQuery<FeeDetail>({
    queryKey: ['amazon-fees', 'detaliu', deschisa],
    enabled: deschisa != null,
    queryFn: async () => (await api.get(`/amazon/fees/${deschisa}`)).data,
  })

  const uploadCsv = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData()
      fd.append('file', file)
      return (await api.post<UploadResult>('/amazon/fees/upload', fd)).data
    },
    onSuccess: (r) => {
      toast.success(
        `Import taxe: ${r.parsed} facturi (${r.created} noi, ${r.updated} actualizate, ${r.skipped} sărite)`,
      )
      qc.invalidateQueries({ queryKey: ['amazon-fees'] })
      if (csvRef.current) csvRef.current.value = ''
    },
    onError: (e) => toast.error(eroare(e, 'Eroare la import')),
  })

  const uploadPdf = useMutation({
    mutationFn: async (files: FileList) => {
      const fd = new FormData()
      Array.from(files).forEach((f) => fd.append('files', f))
      return (await api.post<PublicitateResult>('/amazon/fees/publicitate', fd)).data
    },
    onSuccess: (r) => {
      const text = `Publicitate: ${r.fisiere} fișiere (${r.noi} noi, ${r.actualizate} actualizate). Legate de raportul de tranzacții: ${r.legate}.`
      if (r.erori.length) {
        toast.warning(`${text} Necitite: ${r.erori.map((e) => e.fisier).join(', ')}`)
      } else {
        toast.success(text)
      }
      qc.invalidateQueries({ queryKey: ['amazon-fees'] })
      qc.invalidateQueries({ queryKey: ['amazon-incasari'] })
      if (pdfRef.current) pdfRef.current.value = ''
    },
    onError: (e) => toast.error(eroare(e, 'Eroare la import')),
  })

  const items = data?.items ?? []
  const summary = data?.summary
  const butonCls =
    'whitespace-nowrap rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50'

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Amazon — Facturi servicii</h1>
          <p className="text-sm text-slate-500">
            Facturile Amazon EU (comision, logistică FBA, abonamente, note de credit) din CSV-ul
            „Seller Fee Invoices” și facturile de publicitate Amazon Ads (PDF). Taxare inversă.
          </p>
        </div>
        <div className="flex gap-2">
          <input
            ref={csvRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) uploadCsv.mutate(f)
            }}
          />
          <input
            ref={pdfRef}
            type="file"
            accept=".pdf,application/pdf"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.length) uploadPdf.mutate(e.target.files)
            }}
          />
          <button
            className={butonCls}
            disabled={uploadCsv.isPending}
            onClick={() => csvRef.current?.click()}
          >
            {uploadCsv.isPending ? 'Se încarcă…' : 'Încarcă CSV taxe'}
          </button>
          <button
            className={butonCls}
            disabled={uploadPdf.isPending}
            onClick={() => pdfRef.current?.click()}
          >
            {uploadPdf.isPending ? 'Se încarcă…' : 'Încarcă facturi publicitate'}
          </button>
        </div>
      </div>

      {summary && (
        <Card className="p-4">
          <div className="flex flex-wrap gap-6 text-sm">
            <div>
              <div className="text-slate-500">Facturi</div>
              <div className="text-lg font-semibold">{summary.count}</div>
            </div>
            {Object.entries(summary.byCurrency).map(([cur, val]) => (
              <div key={cur}>
                <div className="text-slate-500">Total {cur}</div>
                <div className="text-lg font-semibold">{fmt(val)}</div>
              </div>
            ))}
            <div>
              <div className="text-slate-500">Total lei</div>
              <div className="text-lg font-semibold">{fmt(summary.totalRon)}</div>
            </div>
          </div>
        </Card>
      )}

      <Card className="p-4">
        <FiltruData
          eticheta="Data facturii"
          de={de}
          pana={pana}
          onChange={(a, b) => (setDe(a), setPana(b))}
        />
      </Card>

      <div className="flex flex-wrap gap-2 text-sm">
        {CATEGORII.map((c) => {
          const n =
            c.key === ''
              ? Object.values(summary?.peCategorii ?? {}).reduce((s, v) => s + v, 0)
              : (summary?.peCategorii[c.key] ?? 0)
          return (
            <button
              key={c.key || 'toate'}
              className={`rounded-full px-3 py-1 ${
                categorie === c.key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'
              }`}
              onClick={() => setCategorie(c.key)}
            >
              {c.label} {summary && <span className="opacity-60">({n})</span>}
            </button>
          )
        })}
      </div>

      <Card className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="border-b bg-slate-50 text-left text-slate-500">
            <tr>
              <th className="px-3 py-2">Nr. factură</th>
              <th className="px-3 py-2">Dată</th>
              <th className="px-3 py-2">Categorie</th>
              <th className="px-3 py-2">Furnizor</th>
              <th className="px-3 py-2 text-right">Sumă</th>
              <th className="px-3 py-2 text-right">Curs BNR</th>
              <th className="px-3 py-2 text-right">Lei</th>
              <th className="px-3 py-2">Plătită</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-slate-400">
                  Se încarcă…
                </td>
              </tr>
            )}
            {!isLoading && items.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-slate-400">
                  Nicio factură. Încarcă un CSV „Seller Fee Invoices” sau facturile de publicitate.
                </td>
              </tr>
            )}
            {items.map((r) => (
              <Fragment key={r.id}>
                <tr
                  className="cursor-pointer border-b last:border-0 hover:bg-slate-50"
                  onClick={() => setDeschisa(deschisa === r.id ? null : r.id)}
                >
                  <td className="px-3 py-2 font-medium">
                    {deschisa === r.id ? '▾' : '▸'} {r.amazonInvoiceNumber}
                  </td>
                  <td className="px-3 py-2">{zi(r.invoiceDate)}</td>
                  <td className="px-3 py-2">
                    {ETICHETA_CATEGORIE[r.categorie ?? ''] ?? r.categorie ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-slate-600">{r.supplierName}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right">
                    {fmt(r.amountNet)} {r.currency}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-500">{r.bnrRate || '—'}</td>
                  <td className="px-3 py-2 text-right font-medium">{fmt(r.amountNetRon)}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs">
                    {r.plata === 'card' ? (
                      'cu cardul'
                    ) : r.retinutaIn ? (
                      <span
                        className="text-green-700"
                        title={`Decontarea ${r.retinutaIn.decontare}`}
                      >
                        din sold, {zi(r.retinutaIn.data)}
                      </span>
                    ) : r.plata === 'sold' ? (
                      <span className="text-slate-500">din sold</span>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
                {deschisa === r.id && (
                  <tr className="border-b bg-slate-50/50">
                    <td colSpan={8} className="px-3 py-3">
                      {detaliu.isLoading && <div className="text-slate-400">Se încarcă…</div>}
                      {detaliu.data && <Defalcare lines={detaliu.data.lines} moneda={r.currency} />}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  )
}

/** Suma pe fiecare tip de taxă din factură (CSV-ul are un rând pe comandă și pe taxă). */
function Defalcare({ lines, moneda }: { lines: FeeDetail['lines']; moneda: string }) {
  const pe = new Map<string, { n: number; suma: number }>()
  for (const l of lines) {
    const t = pe.get(l.feeType) ?? { n: 0, suma: 0 }
    pe.set(l.feeType, { n: t.n + 1, suma: t.suma + l.amount })
  }
  return (
    <table className="text-xs">
      <thead className="text-left text-slate-500">
        <tr>
          <th className="px-2 py-1">Tip taxă</th>
          <th className="px-2 py-1 text-right">Rânduri</th>
          <th className="px-2 py-1 text-right">Sumă ({moneda})</th>
        </tr>
      </thead>
      <tbody>
        {[...pe.entries()]
          .sort((a, b) => Math.abs(b[1].suma) - Math.abs(a[1].suma))
          .map(([tip, v]) => (
            <tr key={tip} className="border-t border-slate-200">
              <td className="px-2 py-1">{tip === '-' ? 'Cupoane („-” în CSV)' : tip}</td>
              <td className="px-2 py-1 text-right">{v.n}</td>
              <td className="px-2 py-1 text-right">{fmt(v.suma)}</td>
            </tr>
          ))}
      </tbody>
    </table>
  )
}
